import { buildColumnTypeMap, cellTypeForColumn } from '../../reporting-core/column-types';
import { ReportCellType } from '../../reporting-core/cell-format';
import { coloredText, escapeHtml, lines, link, renderStandardCell, ReportRenderConfig } from '../../reporting-core/cell-html';
import { getStaffNameDisplay, timeStringFromISO8601 } from '../../reporting-core/utils';
import { getPaymentMethodTypeDisplay, getSingleBookingId, Payment } from '../payment';
import { getContentItem } from '../exports/payments';
import { PaymentMethodType, PaymentStatus, PaymentType } from '../payment-enums';
import {
  BOOKING_DETAILS_QUERY_PARAM,
} from '../../sites/staff-site/components/events/modals/details-modal-query-params';
import {
  COLUMN_NAME_AMOUNT,
  COLUMN_NAME_AMOUNT_PRE_TAX,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_CREDIT_CARD_BRAND,
  COLUMN_NAME_CREDIT_CARD_LAST4,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_LAST_NAME,
  COLUMN_NAME_DATE,
  COLUMN_NAME_DISCOUNT_AMOUNT,
  COLUMN_NAME_DISCOUNT_CODE,
  COLUMN_NAME_DISCOUNT_NAME,
  COLUMN_NAME_INVOICE_DESCRIPTION,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_LOCATION_ID,
  COLUMN_NAME_LOCATION_NAME,
  COLUMN_NAME_PAYMENT_ID,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_PROCESSOR_PAYMENT_ID,
  COLUMN_NAME_REVENUE_ACCOUNTS,
  COLUMN_NAME_STAFF_USER_EMAIL,
  COLUMN_NAME_STAFF_USER_FIRST_NAME,
  COLUMN_NAME_STAFF_USER_ID,
  COLUMN_NAME_STAFF_USER_LAST_NAME,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_TAX_AMOUNT,
  COLUMN_NAME_TIME,
  COLUMN_NAME_TIMESTAMP,
} from '../exports/payments';

export {
  COLUMN_NAME_AMOUNT,
  COLUMN_NAME_AMOUNT_PRE_TAX,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_CREDIT_CARD_BRAND,
  COLUMN_NAME_CREDIT_CARD_LAST4,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_LAST_NAME,
  COLUMN_NAME_DATE,
  COLUMN_NAME_DISCOUNT_AMOUNT,
  COLUMN_NAME_DISCOUNT_CODE,
  COLUMN_NAME_DISCOUNT_NAME,
  COLUMN_NAME_INVOICE_DESCRIPTION,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_LOCATION_ID,
  COLUMN_NAME_LOCATION_NAME,
  COLUMN_NAME_PAYMENT_ID,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_PROCESSOR_PAYMENT_ID,
  COLUMN_NAME_REVENUE_ACCOUNTS,
  COLUMN_NAME_STAFF_USER_EMAIL,
  COLUMN_NAME_STAFF_USER_FIRST_NAME,
  COLUMN_NAME_STAFF_USER_ID,
  COLUMN_NAME_STAFF_USER_LAST_NAME,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_TAX_AMOUNT,
  COLUMN_NAME_TIME,
  COLUMN_NAME_TIMESTAMP,
};

export const COLUMN_NAME_LINKED_CUSTOMER = 'Linked Customer';
export const COLUMN_NAME_FACILITATING_STAFF_USER = 'Facilitating Staff User';

export const COLUMN_HEADER_OVERRIDES: {[key: string]: string} = {
  [COLUMN_NAME_AMOUNT]: 'Total',
};

export const REPORT_COLUMN_NAMES = [
  COLUMN_NAME_DATE,
  COLUMN_NAME_TIME,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_INVOICE_DESCRIPTION,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_AMOUNT,
  COLUMN_NAME_LINKED_CUSTOMER,
  COLUMN_NAME_PAYMENT_ID,
  COLUMN_NAME_FACILITATING_STAFF_USER,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_AMOUNT_PRE_TAX,
  COLUMN_NAME_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_CREDIT_CARD_BRAND,
  COLUMN_NAME_CREDIT_CARD_LAST4,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_CUSTOMER_LAST_NAME,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_STAFF_USER_ID,
  COLUMN_NAME_STAFF_USER_FIRST_NAME,
  COLUMN_NAME_STAFF_USER_LAST_NAME,
  COLUMN_NAME_STAFF_USER_EMAIL,
  COLUMN_NAME_DISCOUNT_AMOUNT,
  COLUMN_NAME_DISCOUNT_CODE,
  COLUMN_NAME_DISCOUNT_NAME,
  COLUMN_NAME_REVENUE_ACCOUNTS,
  COLUMN_NAME_LOCATION_ID,
  COLUMN_NAME_LOCATION_NAME,
  COLUMN_NAME_PROCESSOR_PAYMENT_ID,
];

export const COLUMN_TYPES = buildColumnTypeMap({
  [ReportCellType.objectId]: [
    COLUMN_NAME_INVOICE_ID,
    COLUMN_NAME_PAYMENT_ID,
    COLUMN_NAME_CUSTOMER_ID,
    COLUMN_NAME_LOCATION_ID,
    COLUMN_NAME_STAFF_USER_ID,
    COLUMN_NAME_PROCESSOR_PAYMENT_ID,
  ],
  [ReportCellType.currency]: [
    COLUMN_NAME_AMOUNT_PRE_TAX,
    COLUMN_NAME_TAX_AMOUNT,
    COLUMN_NAME_AMOUNT_REFUNDED,
    COLUMN_NAME_DISCOUNT_AMOUNT,
  ],
  [ReportCellType.date]: [
    COLUMN_NAME_DATE,
    COLUMN_NAME_CUSTOMER_BIRTHDAY,
  ],
  [ReportCellType.custom]: [
    COLUMN_NAME_TIME,
    COLUMN_NAME_INVOICE_DESCRIPTION,
    COLUMN_NAME_LINKED_CUSTOMER,
    COLUMN_NAME_AMOUNT,
    COLUMN_NAME_PAYMENT_METHOD,
    COLUMN_NAME_FACILITATING_STAFF_USER,
  ],
});

/**
 * The HTML shown for a column in the report table, for the emailed report.
 * Mirrors payments.component.html: the multi-line / coloured / linked custom
 * cells; everything else is a standard cell.
 */
export function getDisplayText(payment: Payment, column: string, config: ReportRenderConfig): string {
  const symbol = config.currencySymbol;
  switch (column) {
    case COLUMN_NAME_TIME:
      // config.timezone makes the emailed report (rendered on a UTC host) show the org-local time.
      return coloredText('text-nowrap', timeStringFromISO8601(payment.created_at, config.timezone));
    case COLUMN_NAME_INVOICE_DESCRIPTION: {
      const bookingId = getSingleBookingId(payment);
      let main: string;
      if (!payment.invoice_description) {
        main = '<span class="text-muted">No data</span>';
      } else if (bookingId !== null) {
        main = link(config, '/reporting/bookings', escapeHtml(payment.invoice_description),
                     { query: { [BOOKING_DETAILS_QUERY_PARAM]: bookingId }, target: '_blank' });
      } else {
        main = escapeHtml(payment.invoice_description);
      }
      let discount = '';
      if (payment.discount_amount) {
        discount = '<span class="text-muted d-inline-block mt-1">'
          + '<span class="text-nowrap">' + symbol + escapeHtml(payment.discount_amount) + ' discount applied:</span> '
          + escapeHtml(payment.discount_display_name) + '</span>';
      }
      return lines(main, discount);
    }
    case COLUMN_NAME_LINKED_CUSTOMER:
      return payment.customer_id
        ? link(config, '/customers/' + payment.customer_id + '/payments',
               escapeHtml(payment.customer_first_name) + ' ' + escapeHtml(payment.customer_last_name))
        : coloredText('text-muted', 'Not linked');
    case COLUMN_NAME_AMOUNT: {
      const struck = payment.status === PaymentStatus.partiallyRefunded
        || payment.status === PaymentStatus.refunded
        || payment.status === PaymentStatus.paymentFailed;
      const amount = `<span${struck ? ' class="cm-strikethrough"' : ''}>${symbol}${escapeHtml(payment.amount)}</span>`;
      let extra = '';
      if (payment.status === PaymentStatus.partiallyRefunded) {
        extra = `<span class="text-muted d-inline-block mt-1">Partially refunded (${symbol}${escapeHtml(payment.amount_refunded)})</span>`;
      } else if (payment.status === PaymentStatus.refunded) {
        extra = '<span class="text-muted d-inline-block mt-1">Refunded</span>';
      } else if (payment.status === PaymentStatus.paymentProcessing) {
        extra = '<span class="text-warning d-inline-block mt-1">Processing</span>';
      } else if (payment.status === PaymentStatus.paymentFailed) {
        extra = '<span class="text-danger d-inline-block mt-1">Failed</span>';
      }
      const pending = payment.pending_refund_amount != null
        ? `<span class="text-warning d-inline-block mt-1">Refund of ${symbol}${escapeHtml(payment.pending_refund_amount)} awaiting confirmation from MX Merchant</span>`
        : '';
      return lines(amount, extra, pending);
    }
    case COLUMN_NAME_PAYMENT_METHOD: {
      const main = escapeHtml(getPaymentMethodTypeDisplay(payment.payment_type, payment.payment_method_type));
      let extra = '';
      const processorTypes = [PaymentType.stripe, PaymentType.gravity, PaymentType.mxMerchant, PaymentType.mock];
      if (processorTypes.indexOf(payment.payment_type) !== -1) {
        if (payment.payment_method_type === PaymentMethodType.card && payment.credit_card_last4) {
          const brand = payment.credit_card_brand
            ? `<span style="text-transform: capitalize">${escapeHtml(payment.credit_card_brand)}</span> ` : '';
          extra = `<span class="text-muted d-inline-block mt-1">${brand}ending in ${escapeHtml(payment.credit_card_last4)}</span>`;
        } else if (payment.payment_method_type === PaymentMethodType.bankAccount && payment.bank_account_last4) {
          const bank = payment.bank_account_bank_name
            ? `<span style="text-transform: capitalize">${escapeHtml(payment.bank_account_bank_name)}</span> ` : '';
          extra = `<span class="text-muted d-inline-block mt-1">${bank}ending in ${escapeHtml(payment.bank_account_last4)}</span>`;
        }
      }
      return lines(main, extra);
    }
    case COLUMN_NAME_FACILITATING_STAFF_USER:
      return payment.staff_user_id
        ? escapeHtml(getStaffNameDisplay(payment.staff_user_first_name, payment.staff_user_last_name, payment.staff_user_email))
        : coloredText('text-muted', 'None');
    default:
      return renderStandardCell(getContentItem(payment, column), cellTypeForColumn(COLUMN_TYPES, column), config);
  }
}
